/**
 * FundingFinder — Interactive 3-step funding matching tool
 * Design: Navy section, green results cards, step-by-step form
 */

import React, { useState } from "react";
import { ArrowRight, CheckCircle, Building2, Clock, DollarSign, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const industries = [
  "Retail & E-Commerce", "Restaurant & Food Service", "Healthcare & Medical",
  "Construction & Contracting", "Technology & Software", "Manufacturing",
  "Transportation & Logistics", "Real Estate", "Professional Services", "Other"
];

const yearsOptions = ["Less than 1 year", "1-2 years", "2-5 years", "5-10 years", "10+ years"];

const amountOptions = [
  "$5,000 - $25,000", "$25,000 - $100,000", "$100,000 - $500,000",
  "$500,000 - $1M", "$1M+"
];

const fundingResults = [
  {
    title: "SBA Loan",
    amount: "Up to $5M",
    rate: "4-8% APR",
    time: "2-4 weeks",
    badge: "Best Value",
    badgeColor: "#48d480",
    description: "Government-backed loans with the lowest rates for established businesses.",
    features: ["Lowest interest rates", "Longest repayment terms", "Large loan amounts"],
  },
  {
    title: "Business Line of Credit",
    amount: "Up to $500K",
    rate: "8-15% APR",
    time: "3-5 days",
    badge: "Most Flexible",
    badgeColor: "#e8821c",
    description: "Access funds when you need them with revolving credit.",
    features: ["Draw only what you need", "Revolving credit", "Quick access"],
  },
  {
    title: "Equipment Financing",
    amount: "Up to $2M",
    rate: "6-12% APR",
    time: "1 week",
    badge: "Fast Approval",
    badgeColor: "#1a4a7a",
    description: "Finance equipment purchases with the equipment as collateral.",
    features: ["Equipment as collateral", "Tax benefits", "Preserve cash flow"],
  },
  {
    title: "Business Credit Builder",
    amount: "Up to $50K",
    rate: "12-18% APR",
    time: "Instant",
    badge: "All Credit Types",
    badgeColor: "#7c3aed",
    description: "Build business credit while accessing funding, regardless of credit history.",
    features: ["All credit types accepted", "Build credit score", "Fast approval"],
  },
];

export default function FundingFinder() {
  const [step, setStep] = useState(1);
  const [industry, setIndustry] = useState("");
  const [years, setYears] = useState("");
  const [amount, setAmount] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFind = () => {
    if (industry && years && amount) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setShowResults(true);
      }, 3000);
    }
  };

  const reset = () => {
    setStep(1); setIndustry(""); setYears(""); setAmount(""); setShowResults(false); setIsLoading(false);
  };

  return (
    <section id="funding-finder" data-testid="funding-finder" className="py-20 md:py-28 bg-[#0f2d52] relative overflow-hidden">
      {/* Background triangle motif */}
      <div className="absolute top-0 right-0 opacity-5 pointer-events-none">
        <svg width="400" height="400" viewBox="0 0 400 400">
          <polygon points="200,20 380,380 20,380" stroke="#48d480" strokeWidth="2" fill="none" />
          <polygon points="200,60 340,360 60,360" stroke="#48d480" strokeWidth="2" fill="none" />
          <polygon points="200,100 300,340 100,340" stroke="#48d480" strokeWidth="2" fill="none" />
        </svg>
      </div>

      <div className="container relative z-10">
        {/* Header */}
        <div className="text-center mb-14 fade-up">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#48d480]" />
            <span className="section-label">Funding Finder</span>
            <div className="h-px w-8 bg-[#48d480]" />
          </div>
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            Find Your Perfect Funding Match
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-white/70 mt-3 max-w-xl mx-auto"
          >
            Answer 3 quick questions and we'll match you with the best funding options for your business.
          </p>
        </div>

        {isLoading ? (
          /* Loading State with Triangle Hourglass */
          <div className="max-w-lg mx-auto text-center py-16">
            {/* Animated Triangle Hourglass */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              <svg 
                viewBox="0 0 100 100" 
                className="w-full h-full animate-pulse"
              >
                {/* Top Triangle */}
                <polygon 
                  points="50,10 85,45 15,45" 
                  fill="none" 
                  stroke="#48d480" 
                  strokeWidth="3"
                  className="animate-[spin_2s_ease-in-out_infinite]"
                  style={{ transformOrigin: '50% 50%' }}
                />
                {/* Bottom Triangle (inverted) */}
                <polygon 
                  points="50,90 85,55 15,55" 
                  fill="none" 
                  stroke="#e8821c" 
                  strokeWidth="3"
                  className="animate-[spin_2s_ease-in-out_infinite_reverse]"
                  style={{ transformOrigin: '50% 50%' }}
                />
                {/* Center dots flowing */}
                <circle cx="50" cy="50" r="4" fill="#48d480" className="animate-ping" />
              </svg>
              
              {/* Rotating outer ring */}
              <div className="absolute inset-0 border-4 border-transparent border-t-[#48d480] border-b-[#e8821c] rounded-full animate-spin" />
            </div>

            {/* Loading Text */}
            <h3 
              className="text-2xl font-bold text-white mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Analyzing Your Profile...
            </h3>
            <p className="text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              Finding the best funding options for {industry}
            </p>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="w-3 h-3 rounded-full bg-[#48d480] animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-3 h-3 rounded-full bg-[#48d480] animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-3 h-3 rounded-full bg-[#48d480] animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>

            {/* Powered by Fundi */}
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2.5">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#48d480] to-[#1a7a4a] flex items-center justify-center">
                <span className="text-white text-xs font-bold">F</span>
              </div>
              <span className="text-white/80 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                Powered by <span className="text-[#48d480] font-semibold">Fundi</span>
              </span>
              <span className="text-white/50 text-xs">— Your AI Funding Assistant</span>
            </div>
          </div>
        ) : !showResults ? (
          <div className="max-w-3xl mx-auto">
            {/* Step Indicators */}
            <div className="flex items-center justify-center gap-4 mb-10">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      step >= s
                        ? "bg-[#48d480] text-[#0f2d52]"
                        : "bg-white/10 text-white/50"
                    }`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {step > s ? <CheckCircle size={18} /> : s}
                  </div>
                  {s < 3 && <div className={`h-px w-12 md:w-20 ${step > s ? "bg-[#48d480]" : "bg-white/20"}`} />}
                </div>
              ))}
            </div>

            {/* Step 1: Industry */}
            {step === 1 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 size={22} className="text-[#48d480]" />
                  <h3 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-semibold text-lg">
                    What industry is your business in?
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {industries.map((ind) => (
                    <button
                      key={ind}
                      onClick={() => setIndustry(ind)}
                      data-testid={`industry-${ind.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`p-3 rounded-lg text-sm font-medium text-left transition-all ${
                        industry === ind
                          ? "bg-[#48d480] text-[#0f2d52]"
                          : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => industry && setStep(2)}
                  disabled={!industry}
                  data-testid="step1-continue-btn"
                  className="mt-6 btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* Step 2: Years in Business */}
            {step === 2 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Clock size={22} className="text-[#48d480]" />
                  <h3 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-semibold text-lg">
                    How long have you been in business?
                  </h3>
                </div>
                <div className="space-y-3">
                  {yearsOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setYears(opt)}
                      data-testid={`years-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full p-4 rounded-lg text-sm font-medium text-left flex items-center justify-between transition-all ${
                        years === opt
                          ? "bg-[#48d480] text-[#0f2d52]"
                          : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {opt}
                      {years === opt && <CheckCircle size={18} />}
                    </button>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep(1)} className="btn-outline-white text-sm px-4 py-2.5">Back</button>
                  <button
                    onClick={() => years && setStep(3)}
                    disabled={!years}
                    data-testid="step2-continue-btn"
                    className="btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Funding Amount */}
            {step === 3 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign size={22} className="text-[#48d480]" />
                  <h3 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-semibold text-lg">
                    How much funding do you need?
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {amountOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setAmount(opt)}
                      data-testid={`amount-${opt.replace(/[^a-zA-Z0-9]/g, '')}`}
                      className={`p-4 rounded-lg text-sm font-medium text-left flex items-center justify-between transition-all ${
                        amount === opt
                          ? "bg-[#48d480] text-[#0f2d52]"
                          : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {opt}
                      {amount === opt && <CheckCircle size={18} />}
                    </button>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setStep(2)} className="btn-outline-white text-sm px-4 py-2.5">Back</button>
                  <button
                    onClick={handleFind}
                    disabled={!amount}
                    data-testid="find-options-btn"
                    className="btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Find My Options <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Results */
          <div data-testid="funding-results">
            <div className="text-center mb-8">
              {/* Powered by Fundi Badge */}
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-4">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#48d480] to-[#1a7a4a] flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">F</span>
                </div>
                <span className="text-white/70 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Powered by <span className="text-[#48d480] font-semibold">Fundi</span> — Your AI Funding Assistant
                </span>
              </div>

              <div className="inline-flex items-center gap-2 bg-[#48d480]/20 border border-[#48d480]/30 rounded-full px-4 py-2 mb-4">
                <CheckCircle size={16} className="text-[#48d480]" />
                <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#48d480] text-sm font-semibold">
                  4 Funding Options Found for {industry}
                </span>
              </div>
              <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-sm">
                Based on your profile: {years} in business, seeking {amount}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
              {fundingResults.map((result) => (
                <div
                  key={result.title}
                  className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div
                    className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full mb-4"
                    style={{ backgroundColor: result.badgeColor, fontFamily: "'Inter', sans-serif" }}
                  >
                    {result.badge}
                  </div>
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-[#0f2d52] font-bold text-lg mb-1"
                  >
                    {result.title}
                  </h3>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-2xl font-black text-[#48d480] mb-1">{result.amount}</div>
                  <div className="flex gap-3 mb-3">
                    <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-xs text-gray-500">{result.rate}</span>
                    <span className="text-gray-300">|</span>
                    <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-xs text-gray-500">{result.time}</span>
                  </div>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 text-xs mb-4 leading-relaxed">{result.description}</p>
                  <ul className="space-y-1.5 mb-5">
                    {result.features.map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#48d480]" />
                        <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-xs text-gray-600">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/funding-options"
                    className="flex items-center gap-1 text-[#0f2d52] text-xs font-bold hover:text-[#48d480] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Learn More <ChevronRight size={14} />
                  </Link>
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center mb-6">
              <h3 className="text-white text-2xl font-bold mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Ready to Get Funded?
              </h3>
              <p className="text-white/70 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                Apply now and one of our funding specialists will help you secure the best option for your business.
              </p>
              <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer">
                <button className="bg-[#e8821c] hover:bg-[#d67518] text-white font-bold py-4 px-10 rounded-lg transition-all flex items-center justify-center gap-2 mx-auto shadow-lg shadow-orange-900/30 text-lg">
                  Apply Now <ArrowRight size={20} />
                </button>
              </a>
            </div>

            <div className="text-center">
              <button onClick={reset} className="text-white/60 hover:text-white text-sm underline">
                Start Over
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
